// Plan 30-03 Task 1 — LetterLayer: unified shared field consumer of
// PlayerView.board (the Runespring cell shape established by Plan 30-01's
// schema mirror).
//
// REWRITE from the Plan 08-04 two-region (private + shared) renderer to
// the unified shared-field topology per UI-SPEC §1. One LetterCubeSprite
// per board cell, mounted via LetterCubeSpriteMount (@pixi/react adapter
// — Plan 30-03 Task 1). Per-cell state diff drives sprite state
// transitions via LetterCubeSprite.updateCell + setState (Plan 30-02).
//
// Cleanup vs the old layer:
//   - Deleted: inline letter-cube rect+text component (replaced by the
//     imperative LetterCubeSprite class from Plan 30-02).
//   - Deleted: the legacy player-rack consumption (the corresponding
//     schema fields were removed by Plan 30-01).
//   - Deleted: the legacy revealed-shared consumption (same).
//   - Deleted: the "isPrivate" border distinction (unified field has no
//     private/shared boundary — tier embellishments + tint encode owner +
//     rarity instead).
//   - Kept: `cellIdxToScreen` (board-layout.ts) — the vertical flip for
//     "your row at the bottom" is still a meaningful UX affordance per
//     RESEARCH §2.11 and UI-SPEC §2 (DEC-023 1:1 alignment with the
//     parallel-DOM MatchGrid).
//
// LIFECYCLE:
//   - On mount: install the single tweedle-Pixi-ticker driver (Pitfall 5
//     lock from Plan 30-01) keyed to the active Pixi Application. Tear
//     down on unmount.
//   - Theme switch or reducedMotion flip: re-key the layer so the entire
//     LetterCubeSprite cohort is destroyed + reconstructed. Cheap
//     (~60 sprites) and correct (sprites capture reducedMotion at
//     construction per UI-SPEC §5; theme tokens flow through theme prop).
//   - Single shared tweedle Group + single shared ParticleEmitter at the
//     LetterLayer level — T-30-02 perf mitigation (one ticker.add for the
//     whole layer, not 60).
//
// A11Y WRAPPER:
//   - The Pixi canvas is opaque to axe — the descriptive aria-label lives
//     on the wrapper DOM node hosted by MatchCanvas / GameCanvas (UI-SPEC
//     §7). LetterLayer EXPORTS `buildBoardAriaLabel(playerView)` for that
//     consumer; LetterLayer itself renders the Pixi subtree only.
//   - jest-axe coverage is the LetterLayer.a11y.test.tsx test in this
//     plan's Task 1 deliverables.

import { useStore } from '@nanostores/react'
import { useApplication } from '@pixi/react'
import type { JSX } from 'react'
import { useEffect, useMemo, useRef } from 'react'
import { Group } from 'tweedle.js'
import { $reducedMotion } from '../../stores/motion'
import { useParticleSystem } from '../motion/ParticleSystemContext'
import { ParticleEmitter } from '../motion/particles'
import type { PlayerView } from '../PlayerViewSchema'
import { LetterCubeSpriteMount } from '../sprites/LetterCubeSpriteMount'
import { useThemeAtlas } from '../theme/atlas-loader'
import { extractRuneThemeTokens } from '../theme/extract-rune-tokens'
import { useTheme } from '../theme/ThemeProvider'
import { installTweedleTickerDriver } from '../theme/tweedle-bootstrap'
import { resolveWordField } from '../wire-grids'
import { buildBoardAriaLabel } from './board-aria-label'
import {
  boardRows,
  CELL_SIZE_PX,
  cellIdxToScreen,
  centerGrowWindow,
  finalCellKey,
} from './board-layout'
import { defaultTextureFactory } from './default-texture-factory'

// Re-export the aria-label generator so MatchCanvas / GameCanvas can pull
// it via the LetterLayer module (UI-SPEC §7 colocation). Tests import the
// Pixi-free shim directly from `./board-aria-label`.
export { buildBoardAriaLabel }

interface LetterLayerProps {
  playerView: PlayerView
}

/**
 * LetterLayer — unified shared-field cell renderer. Maps every cell in
 * `playerView.board` to a LetterCubeSprite mounted via the @pixi/react
 * adapter. Theme + reducedMotion changes re-key the whole subtree so
 * sprites reconstruct fresh (sprites capture reducedMotion at construction
 * per UI-SPEC §5).
 */
export function LetterLayer({ playerView }: LetterLayerProps): JSX.Element {
  const yourSlot = playerView.your_slot
  const { descriptor } = useTheme()
  const reducedMotion = useStore($reducedMotion)
  const app = useApplication().app

  // Shared tweedle Group — T-30-02 perf mitigation (one ticker.add for the
  // whole layer). Uses Group.shared because installTweedleTickerDriver
  // (Plan 30-01 / tweedle-bootstrap.ts) ticks only Group.shared; a
  // private Group would create dead tweens. The single-Group + single-
  // RAF discipline survives — Group.shared IS the single shared instance.
  const tweenGroupRef = useRef<Group>(Group.shared)

  // Shared ParticleEmitter — caller throttles per-cell rates. Polish Phase 2:
  // connect it to the match's shared GPU ParticleSystem so cube-lifecycle
  // emits (rune-carve / stone-fall / stone-carve) actually render.
  const system = useParticleSystem()
  const particlesRef = useRef<ParticleEmitter | null>(null)
  if (particlesRef.current === null) {
    particlesRef.current = new ParticleEmitter()
    particlesRef.current.connect(system)
  }
  // Keep connected to the current system instance (stable across LetterLayer's
  // theme/reduced-motion re-keys since the provider lives above World).
  useEffect(() => {
    particlesRef.current?.connect(system)
  }, [system])

  // Install the tweedle-Pixi-ticker driver once per Pixi Application
  // lifecycle. Teardown on unmount drains Group.shared (Pitfall 5 lock).
  useEffect(() => {
    if (!app) return undefined
    const teardown = installTweedleTickerDriver(app.ticker)
    return teardown
  }, [app])

  // Atlas textures — typed Texture refs for the 6 Runespring frames + the
  // dynamic per-letter cube_letter_X lookup (Plan 30-01).
  const textures = useThemeAtlas(
    // descriptor may be null during first paint — fall back to a
    // placeholder; the layer also conditionally renders when descriptor
    // is ready, but useMemo deps must be stable.
    (descriptor ?? ({} as never)) as never,
    defaultTextureFactory,
  )

  // Memoize theme-token extraction — runs only on descriptor identity flip.
  const themeTokens = useMemo(() => {
    if (!descriptor) return null
    return extractRuneThemeTokens(descriptor)
  }, [descriptor])

  // Re-key the layer on theme or reducedMotion flip so the entire
  // LetterCubeSprite cohort is destroyed + reconstructed (sprites capture
  // reducedMotion at construction per UI-SPEC §5).
  const layerKey = `${descriptor?.theme_key ?? 'none'}-${
    reducedMotion ? 'rm' : 'fm'
  }`

  if (!descriptor || !themeTokens) {
    // Theme still loading — render empty container; ThemeProvider's
    // fallback handles the "still loading" CSS-vars boot per Plan 08-03.
    return <pixiContainer label="LetterLayer" />
  }

  // Phase 31 Plan 31-04 (RUNE-32) — caller's affinity letters threaded into
  // each LetterCubeSpriteMount so the sprite-level updateAffinity flips the
  // Phase 30 _affinityHalo slot's visibility for caller-affinity-matched
  // cells. Reference stable across renders until the server emits a new
  // PlayerView with a rotated tuple (Mark mutations).
  const affinityLetters = playerView.your_affinity_letters

  // ARCA-749 (WS3) — the letters view renders the WORD field, a grid decoupled
  // from the (separate, fixed 6×6) chess board. This is the surface that GROWS
  // 36 → 48 → 60 via Flop/River; reveals act here only (the chess grid never
  // animates/grows). Falls back to a board-derived slice for partial fixtures.
  const field = resolveWordField(playerView)

  // Live row count drives the vertical flip as the field grows (36→48→60).
  const rows = boardRows(field.length)

  // Phase 2 (court-opens-at-the-center) — the NEW center-inserted cohort for the
  // CURRENT grown board. Backend deepens the middle, so the new cells live in a
  // contiguous center window ([18,30) flop / [24,36) river); null pre-flop. This
  // is the authoritative new-cohort signal — the old `cell_idx>=36/48` windows
  // are STALE (they are SHIFTED existing cells after the MID growth).
  const w = centerGrowWindow(field.length)

  return (
    <pixiContainer label="LetterLayer" key={layerKey}>
      {field.map((cell) => {
        const { screen_x, screen_y } = cellIdxToScreen(
          cell.cell_idx,
          yourSlot,
          rows,
        )
        const isNewThisGrow = !!(
          w &&
          cell.cell_idx >= w.start &&
          cell.cell_idx < w.end
        )
        return (
          // Phase 2: key on the STABLE final-60-cell slot (finalCellKey), NOT
          // the current cell_idx. The MID growth SHIFTS surviving cells'
          // cell_idx, so keying on cell_idx would remount + teleport survivors
          // at each reveal; finalCellKey is invariant across both reveals, so a
          // surviving sprite keeps its key and GLIDES to its new screen cell.
          <LetterCubeSpriteMount
            key={`cell-${finalCellKey(cell.cell_idx, field.length)}`}
            cell={cell}
            theme={themeTokens}
            reducedMotion={reducedMotion}
            textures={textures}
            tweenGroup={tweenGroupRef.current}
            particles={particlesRef.current ?? new ParticleEmitter()}
            x={screen_x * CELL_SIZE_PX}
            y={screen_y * CELL_SIZE_PX}
            affinityLetters={affinityLetters}
            currentTurnIdx={playerView.current_turn_idx}
            isNewThisGrow={isNewThisGrow}
            cohortOrdinal={isNewThisGrow && w ? cell.cell_idx - w.start : 0}
          />
        )
      })}
    </pixiContainer>
  )
}
